type TelemetryScope = {
  setContext: (key: string, value: unknown) => void;
  setLevel: (level: string) => void;
  setTag: (key: string, value: unknown) => void;
};

type BreadcrumbPayload = {
  event: string;
  meta?: Record<string, unknown>;
  timestamp: number;
};

type ErrorPayload = {
  error: unknown;
  meta?: Record<string, unknown>;
  timestamp: number;
};

const noop = () => {};

const listeners: {
  breadcrumb: ((payload: BreadcrumbPayload) => void)[];
  error: ((payload: ErrorPayload) => void)[];
} = {
  breadcrumb: [],
  error: [],
};

function emitBreadcrumb(payload: BreadcrumbPayload) {
  listeners.breadcrumb.forEach(listener => listener(payload));
}

function emitError(payload: ErrorPayload) {
  listeners.error.forEach(listener => listener(payload));
}

export function breadcrumb(event: string, meta?: Record<string, unknown>): void {
  emitBreadcrumb({ event, meta, timestamp: Date.now() });
}

export function trackError(error: unknown, meta?: Record<string, unknown>): void {
  emitError({ error, meta, timestamp: Date.now() });
}

export function capture(error: unknown, meta?: Record<string, unknown>): void {
  trackError(error, meta);
}

export function message(event: string, meta?: Record<string, unknown>): void {
  breadcrumb(event, meta);
}

export async function withScope<T>(fn: (scope: TelemetryScope) => Promise<T> | T): Promise<T> {
  const scope: TelemetryScope = {
    setContext: noop,
    setLevel: noop,
    setTag: noop,
  };
  return fn(scope);
}

export function onBreadcrumb(listener: (payload: BreadcrumbPayload) => void): () => void {
  listeners.breadcrumb.push(listener);
  return () => {
    const index = listeners.breadcrumb.indexOf(listener);
    if (index >= 0) listeners.breadcrumb.splice(index, 1);
  };
}

export function onError(listener: (payload: ErrorPayload) => void): () => void {
  listeners.error.push(listener);
  return () => {
    const index = listeners.error.indexOf(listener);
    if (index >= 0) listeners.error.splice(index, 1);
  };
}

export type { TelemetryScope };
